import { get } from 'lodash';
import { useRouter } from 'next/router';
import type { SyntheticEvent } from 'react';

import type { IClientMicrocreditListItem } from '@src/@types/clienttypes';
import type { AffSub4 } from '@src/@types/microcredits';
import { useCardClick } from '@src/components/ProductList/components/Card/useCardClick';
import { PIXELS_TYPES } from '@src/constants/analytics';
import { MONETIZATION_INTERNAL } from '@src/helpers/advertising';
import { sendAdvertisingButtonClickEvent } from '@src/helpers/analyticsEvents';
import { open } from '@src/helpers/navigationHelper';
import { usePixel } from '@src/hooks/usePixel';
import { affiliatesPageRouteWithSpecificAffIdAndSource } from '@src/shared/constants/pageRoutes';
import { getRoutePathParams } from '@src/utils/getRoutePathParams';
import { useHasOfferFix } from 'entities/Microcredits/lib';
import type { LightweightCardOnClickArgs } from 'entities/Microcredits/ui/LightweightCard';

export const useCardAnalytics = (
  offer: IClientMicrocreditListItem,
  position: number,
  onClick?: (e: SyntheticEvent, args: LightweightCardOnClickArgs) => void,
) => {
  const router = useRouter();

  const routeParams = getRoutePathParams(affiliatesPageRouteWithSpecificAffIdAndSource, router.asPath);

  const { getIsEnableRedirectToFullDeal, navigateToFullDealLandingPage } = useHasOfferFix();

  const { organization, advertising, name, _id } = offer;

  const { sourceLink, pixelDisplayLink } = useCardClick({ offer, position });

  const source = get(routeParams, 'source', `${sourceLink}|position_${position}`);
  const affSub4: AffSub4 = {
    organizationName: organization.name,
    productName: name,
    organizationId: organization._id,
    productId: _id,
  };

  const { pixelLink: mfoSiteLink, createPixelLink } = usePixel(advertising?.monetization?.pixels?.click, {
    sub: advertising.advSub,
    affSub4,
    type: advertising?.productType,
    source,
    pixelType: PIXELS_TYPES.CLICK,
  });

  const openMfoSite = (e: SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const mfoUrl = createPixelLink();

    if (getIsEnableRedirectToFullDeal() && mfoUrl?.startsWith('https://sravni.go2cloud.org')) {
      navigateToFullDealLandingPage();

      return;
    }

    if (onClick) {
      onClick(e, {
        mfoUrl,
        id: offer._id,
      });

      return;
    }

    sendAdvertisingButtonClickEvent(offer);

    if (!mfoUrl) {
      return;
    }

    if (advertising?.monetization?.kind === MONETIZATION_INTERNAL) {
      router.push(mfoUrl);
      return;
    }

    open({ href: mfoUrl, target: '_blank', features: 'noopener' });
  };

  return { mfoSiteLink, source, affSub4, pixelDisplayLink, openMfoSite };
};
